/**
 * ReviewQueueRow — one review job: blocker or finding notes, status badges, evidence links and its actions. Demo page:
 * /email (a message row with actions).
 */
import { Link } from 'react-router-dom';
import { AlertTriangle, ChevronRight, Eye, Play, RefreshCw } from 'lucide-react';
import { Badge, Button, Callout } from '../../../components/ui/index.js';
import { buttonStyles } from '../../../components/tailgrids/core/button.js';
import { daemonRunPath } from '../catalogReviewLinks.js';
import type { DaemonReviewQueueItem } from './catalog-types.js';
import { sentenceCase } from '../../../components/ui/FilterSelect.js';
import { statusVariant } from './catalog-helpers.js';

export function ReviewQueueRow({
  item,
  onOpenCandidate,
  onEnableDaemon,
  onRetryItem,
  onProcessDaemon,
  busy,
}: {
  item: DaemonReviewQueueItem;
  onOpenCandidate: (candidateId: number) => void;
  onEnableDaemon: (daemonId: number, daemonName: string) => void;
  onRetryItem: (itemId: number) => void;
  onProcessDaemon: (daemonName: string) => void;
  busy: boolean;
}) {
  const daemonDisabled = item.targetDaemonEnabled === false;
  const candidateFailed = item.candidateStatus === 'security_failed' || item.candidateStatus === 'quality_failed';
  const blocker = item.blocker;
  const catalogDataBlocked = blocker?.type === 'catalog_data_blocked';
  return (
    <div className="p-4 text-sm">
      {item.status === 'blocked' && blocker && (
        <Callout tone="danger" icon={AlertTriangle} title={blocker.title} className="mb-3">
          <p>{blocker.message}</p>
          {blocker.type === 'permission_blocked' && (
            <p className="mt-1">
              This is a permission blocker, not a candidate decision. Grant or revoke scoped permissions from the daemon
              run audit surface.
            </p>
          )}
          {catalogDataBlocked && (
            <p className="mt-1">
              This usually means the discovery is still source-only or missing a source-backed payload. Review the
              candidate provenance panel and source evidence before retrying.
            </p>
          )}
        </Callout>
      )}
      {item.status === 'blocked' && !blocker && (
        <Callout tone="danger" icon={AlertTriangle} title="Review is blocked, not failed" className="mb-3">
          Open the candidate and daemon evidence, then retry this review when the blocker is resolved.
        </Callout>
      )}
      {candidateFailed && item.status === 'completed' && (
        <Callout tone="warning" icon={AlertTriangle} title="Review completed with findings" className="mb-3">
          Candidate is now {item.candidateStatus?.replaceAll('_', ' ')}. Quality review only runs after security review
          passes.
        </Callout>
      )}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-heading">{item.targetDaemonName}</span>
            <Badge
              variant={
                item.status === 'blocked' || item.status === 'failed'
                  ? 'danger'
                  : item.status === 'completed'
                    ? 'success'
                    : item.status === 'pending'
                      ? 'warning'
                      : 'neutral'
              }
            >
              {sentenceCase(item.status)}
            </Badge>
            <Badge variant="neutral">{sentenceCase(item.eventType)}</Badge>
            {item.candidateTargetType && <Badge variant="neutral">{sentenceCase(item.candidateTargetType)}</Badge>}
            {item.candidateStatus && (
              <Badge variant={statusVariant(item.candidateStatus)}>
                Candidate {sentenceCase(item.candidateStatus)}
              </Badge>
            )}
            {item.targetDaemonEnabled === false && <Badge variant="danger">daemon disabled</Badge>}
          </div>
          <div className="text-xs text-muted mt-2">
            {item.attempts > 0
              ? `${item.attempts} review attempt${item.attempts === 1 ? '' : 's'}`
              : 'Not reviewed yet'}
            {item.updatedAt ? ` / updated ${new Date(item.updatedAt).toLocaleString()}` : ''}
          </div>
          {item.candidateSummary && <div className="text-xs text-muted mt-2 line-clamp-2">{item.candidateSummary}</div>}
          {item.lastError && <div className="text-xs text-danger mt-2">{item.lastError}</div>}
          {item.updatedAt && (
            <div className="text-xs text-faint mt-2">Updated {new Date(item.updatedAt).toLocaleString()}</div>
          )}
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            {item.sourceEntityId && item.sourceDaemonRunId && (
              <Link
                to={daemonRunPath(item.sourceEntityId, item.sourceDaemonRunId)}
                className="inline-flex items-center gap-1 text-accent-text hover:underline"
              >
                Source run evidence <ChevronRight size={13} />
              </Link>
            )}
            {item.sourceEntityId && item.sourceFindingId && (
              <Link
                to={`/admin/daemons/${item.sourceEntityId}?tab=findings`}
                className="inline-flex items-center gap-1 text-accent-text hover:underline"
              >
                Source finding {item.sourceFindingId} <ChevronRight size={13} />
              </Link>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => onOpenCandidate(item.candidateId)}>
            <Eye size={14} /> Review candidate
          </Button>
          {catalogDataBlocked && (
            <Link
              to="/agents/libraries/catalog-review?tab=sources"
              className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}
            >
              Catalog sources
            </Link>
          )}
          {item.targetDaemonId && item.targetDaemonEnabled === false && (
            <Button
              size="sm"
              variant="success"
              disabled={busy}
              onClick={() => onEnableDaemon(item.targetDaemonId!, item.targetDaemonName)}
            >
              <Play size={14} /> Enable and retry
            </Button>
          )}
          {(item.status === 'failed' ||
            (item.status === 'blocked' && !daemonDisabled) ||
            (item.status === 'completed' && candidateFailed)) && (
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => onRetryItem(item.id)}>
              <RefreshCw size={14} /> Retry
            </Button>
          )}
          {item.status === 'pending' && (
            <Button
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => onProcessDaemon(item.targetDaemonName)}
            >
              <Play size={14} /> Run now
            </Button>
          )}
          {item.targetDaemonId ? (
            <Link
              to={`/admin/daemons/${item.targetDaemonId}`}
              className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}
            >
              Open daemon
            </Link>
          ) : (
            <Link
              to="/admin/daemons"
              className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}
            >
              Find daemon
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
